/**
 * An IOFlow flow (nav obj_type "J") opened from the navigation menu —
 * docs/flow_forms.md.
 *
 * Laid out like IOFlow's own run screen: Input / Logs / Results, plus the
 * flow's History. What the Input tab holds is decided by the flow's OWN
 * trigger, read at open — nobody configures it twice:
 *
 *   manual    → the flow's input form: a transaction screen generated from the
 *               flow's declared inputs (header, detail grids, item_change,
 *               validations), whose one button runs the flow;
 *   scheduled → next run and cadence, and Run now for people who build the app;
 *   anything else → it runs on its own; its runs are listed under History.
 *
 * Runs and results are read live from IOFlow; Twasta keeps only the record of
 * who pressed Run. An end user sees the runs they started; people who build
 * the app see every run of the flow.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import {
  schedulerColors as C, Chip, DriftNotice, Empty, cardStyle, formatDuration,
  formatRelative, formatWhen, labelStyle,
} from '@twasta/page-runtime/shell/jobs'
import { sanitizeRichHtml } from '@twasta/page-runtime/richContent'
import type { TransactionModel, TransactionRoot } from '@twasta/page-runtime/txn/types'
import {
  createFlowForm, getFlowMenu, getFlowMenuRun, listFlowMenuRuns, runFlowMenu,
  syncFlowForm, type FlowMenuInfo, type FlowRun,
} from '@/api/flowForms'
import { parseApiError } from '@twasta/page-runtime/txn'
import { modelOverlayGet } from '@/livepreview/api'
import { loadModel } from '@/livepreview/runtime/loadModel'
import FlowFormScreen from './FlowFormScreen'

type Tab = 'input' | 'logs' | 'results' | 'history'

const POLL_MS = 1500

function errorText(e: unknown, fallback: string): string {
  try {
    const parsed = parseApiError(e)
    if (parsed?.message) return parsed.message
  } catch { /* fall through */ }
  const err = e as { response?: { data?: { detail?: unknown } } }
  const detail = err?.response?.data?.detail
  if (typeof detail === 'string') return detail
  return e instanceof Error ? e.message : fallback
}

export default function FlowRunPane({ project, objName, showDesignLink = true }: {
  project: string
  /** The menu node's object name — also the name of the flow's input form. */
  objName: string
  /** Offer "Edit form" (off when the pane is already inside the designer). */
  showDesignLink?: boolean
}) {
  const navigate = useNavigate()
  const [info, setInfo] = useState<FlowMenuInfo | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [model, setModel] = useState<TransactionModel | null>(null)
  const [modelError, setModelError] = useState<string | null>(null)
  const [formKey, setFormKey] = useState(0)
  const [tab, setTab] = useState<Tab>('input')
  const userPickedTab = useRef(false)
  const [current, setCurrent] = useState<FlowRun | null>(null)
  const [following, setFollowing] = useState(false)
  const [runs, setRuns] = useState<FlowRun[] | null>(null)
  const [busy, setBusy] = useState<string | null>(null)

  const loadInfo = useCallback(async () => {
    try {
      const res = await getFlowMenu(project, objName)
      setInfo(res)
      setError(null)
      return res
    } catch (e: unknown) {
      setError(errorText(e, 'Could not reach IOFlow'))
      return null
    }
  }, [project, objName])

  const loadRuns = useCallback(async () => {
    try {
      const res = await listFlowMenuRuns(project, objName, 40)
      setRuns(res.executions || [])
    } catch {
      setRuns([])
    }
  }, [project, objName])

  // The effective model (this organization's presentation overlay merged),
  // exactly as the Run view loads any transaction screen.
  const loadForm = useCallback(async () => {
    setModelError(null)
    try {
      const res = await modelOverlayGet(project, objName, 'T')
      const eff = (res.effective as unknown as TransactionRoot)?.transaction
      setModel(eff || await loadModel(project, objName))
    } catch {
      try {
        setModel(await loadModel(project, objName))
      } catch (e: unknown) {
        setModel(null)
        setModelError(errorText(e, 'The input form could not be loaded'))
      }
    }
    setFormKey((k) => k + 1)
  }, [project, objName])

  useEffect(() => { void loadInfo() }, [loadInfo])
  useEffect(() => { void loadRuns() }, [loadRuns])
  const formExists = !!info?.form.exists
  const manual = !!info?.flow.manual
  useEffect(() => {
    if (formExists && manual) void loadForm()
  }, [formExists, manual, loadForm])

  // Follow a started run until IOFlow says it is over; then show the result,
  // as IOFlow does (unless the person picked a tab themselves).
  useEffect(() => {
    if (!following || !current?.execution_id) return undefined
    let cancelled = false
    const timer = window.setTimeout(async () => {
      try {
        const res = await getFlowMenuRun(project, objName, current.execution_id)
        if (cancelled) return
        setCurrent(res.execution)
        if (res.execution.final) {
          setFollowing(false)
          if (!userPickedTab.current) setTab('results')
          if (res.execution.status === 'completed') toast.success('Flow finished')
          else if (res.execution.status === 'failed') toast.error('The flow failed')
          void loadRuns()
        }
      } catch (e: unknown) {
        if (cancelled) return
        // Not visible yet (IOFlow writes the run as it starts): keep waiting,
        // but stop on a refusal that will not change.
        const status = (e as { response?: { status?: number } })?.response?.status
        if (status === 403 || status === 422) {
          setFollowing(false)
          toast.error(errorText(e, 'IOFlow did not return the run'))
        } else {
          setCurrent((c) => (c ? { ...c } : c))
        }
      }
    }, POLL_MS)
    return () => { cancelled = true; window.clearTimeout(timer) }
  }, [following, current, project, objName, loadRuns])

  const started = useCallback((executionId: string) => {
    if (!executionId) {
      toast.error('IOFlow accepted the run but returned no execution id')
      return
    }
    toast.success('Flow started')
    userPickedTab.current = false
    setCurrent({
      execution_id: executionId, status: 'running', final: false, trigger_type: 'manual',
      started_at: new Date().toISOString(), finished_at: null, duration_seconds: null,
      current_step: null, logs: [],
    })
    setFollowing(true)
    setTab('logs')
  }, [])

  const runNow = useCallback(async () => {
    setBusy('run')
    try {
      const res = await runFlowMenu(project, objName)
      started(res.execution_id)
    } catch (e: unknown) {
      toast.error(errorText(e, 'IOFlow refused the run'))
    } finally {
      setBusy(null)
    }
  }, [project, objName, started])

  const createForm = useCallback(async () => {
    if (!info) return
    setBusy('create')
    try {
      await createFlowForm(project, objName, info.flow.flow_id)
      toast.success('Input form created')
      await loadInfo()
    } catch (e: unknown) {
      toast.error(errorText(e, 'The input form could not be created'))
    } finally {
      setBusy(null)
    }
  }, [info, project, objName, loadInfo])

  const resync = useCallback(async () => {
    setBusy('sync')
    try {
      const res = await syncFlowForm(project, objName)
      const r = res.report
      const parts = [
        r.added.length ? `${r.added.length} added` : '',
        r.removed.length ? `${r.removed.length} removed` : '',
        r.refreshed.length ? `${r.refreshed.length} updated` : '',
      ].filter(Boolean)
      toast.success(parts.length ? `Form updated: ${parts.join(', ')}` : 'The form already matches the flow')
      await loadInfo()
      await loadForm()
    } catch (e: unknown) {
      toast.error(errorText(e, 'The input form could not be updated'))
    } finally {
      setBusy(null)
    }
  }, [project, objName, loadInfo, loadForm])

  const openRun = useCallback(async (run: FlowRun) => {
    userPickedTab.current = true
    setCurrent(run)
    setFollowing(!run.final)
    setTab('results')
    try {
      const res = await getFlowMenuRun(project, objName, run.execution_id)
      setCurrent(res.execution)
      setFollowing(!res.execution.final)
    } catch (e: unknown) {
      toast.error(errorText(e, 'IOFlow did not return that run'))
    }
  }, [project, objName])

  const pickTab = (t: Tab) => {
    userPickedTab.current = true
    setTab(t)
  }

  if (error) {
    return <Empty title="This flow is not available" hint={error} />
  }
  if (!info) {
    return <div style={{ padding: '2rem', textAlign: 'center', color: C.ink2,
      fontSize: '0.9125rem' }}>Loading…</div>
  }
  const flow = info.flow
  const form = info.form
  const builder = info.can_design

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, height: '100%', minHeight: 0 }}
      data-tw-screen={objName} data-tw-kind="J">
      {/* ── header ─────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ fontSize: '1.0625rem', fontWeight: 600, color: C.ink }}>{flow.name}</div>
          {flow.description && (
            <div style={{ fontSize: '0.8825rem', color: C.ink2, marginTop: 2 }}>{flow.description}</div>
          )}
        </div>
        <Chip tone="muted">{titleCase(flow.scheduled ? 'scheduled' : flow.trigger_type)}</Chip>
        <Chip tone={flow.active ? 'ok' : 'warn'}>
          {flow.active ? 'Active' : titleCase(flow.status) || 'Inactive'}
        </Chip>
        {builder && manual && form.exists && (
          <button type="button" className="btn btn-secondary btn-sm" disabled={!!busy}
            onClick={() => void resync()} data-tw-action="flow-resync"
            title="Bring the form in line with the flow's inputs in IOFlow — what you designed is kept">
            <span className="material-icons" style={{ fontSize: 16 }}>sync</span>
            {busy === 'sync' ? 'Updating…' : 'Update form from flow'}
          </button>
        )}
        {builder && manual && form.exists && showDesignLink && (
          <button type="button" className="btn btn-secondary btn-sm"
            onClick={() => navigate(`/designers/transaction/${encodeURIComponent(project)}/${encodeURIComponent(objName)}`)}
            data-tw-action="flow-design"
            title="Item change, validations, look-ups and layout — in the transaction designer">
            <span className="material-icons" style={{ fontSize: 16 }}>edit</span>
            Design form
          </button>
        )}
      </div>

      {!flow.active && (
        <Notice tone="warn" icon="pause_circle">
          This flow is not active in IOFlow ({flow.status || 'inactive'}). IOFlow may refuse to run it until it is activated.
        </Notice>
      )}
      {manual && form.exists && form.stale && (
        <Notice tone="warn" icon="sync_problem">
          {builder
            ? 'The flow\'s inputs changed in IOFlow since this form was made. Update the form from the flow to pick the changes up — what you designed is kept.'
            : 'The flow\'s inputs changed in IOFlow since this form was made. Ask the person who builds this application to update it.'}
        </Notice>
      )}
      {builder && manual && form.exists && form.notes.length > 0 && (
        <Notice tone="info" icon="info">
          {form.notes.map((n) => <div key={n}>{n}</div>)}
        </Notice>
      )}

      {/* ── tabs ───────────────────────────────────────────────── */}
      <div role="tablist" style={{ display: 'flex', gap: 2, borderBottom: `1px solid ${C.line}` }}>
        <TabButton active={tab === 'input'} onClick={() => pickTab('input')} hook="input">Input</TabButton>
        <TabButton active={tab === 'logs'} onClick={() => pickTab('logs')} hook="logs">
          Logs{following && <span style={{ marginLeft: 6, color: C.info }}>●</span>}
        </TabButton>
        <TabButton active={tab === 'results'} onClick={() => pickTab('results')} hook="results">Results</TabButton>
        <TabButton active={tab === 'history'} onClick={() => { pickTab('history'); void loadRuns() }} hook="history">
          History{runs && runs.length > 0 ? ` (${runs.length})` : ''}
        </TabButton>
      </div>

      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {/* The form stays mounted while another tab shows, so what was typed
            survives a look at the results. */}
        <div style={{ display: tab === 'input' ? 'flex' : 'none', flex: 1, minHeight: 480,
          flexDirection: 'column' }}>
          <InputTab
            info={info} model={model} modelError={modelError} formKey={formKey}
            project={project} objName={objName} busy={busy}
            onStarted={started} onRunNow={() => void runNow()} onCreate={() => void createForm()}
          />
        </div>
        {tab === 'logs' && <LogsTab run={current} following={following} />}
        {tab === 'results' && <ResultsTab run={current} following={following} />}
        {tab === 'history' && (
          <HistoryTab runs={runs} allRuns={info.all_runs} selected={current?.execution_id || null}
            onOpen={(r) => void openRun(r)} onRefresh={() => void loadRuns()} />
        )}
      </div>
    </div>
  )
}

// ── Input ────────────────────────────────────────────────────────────────

function InputTab({ info, model, modelError, formKey, project, objName, busy,
  onStarted, onRunNow, onCreate }: {
  info: FlowMenuInfo
  model: TransactionModel | null
  modelError: string | null
  formKey: number
  project: string
  objName: string
  busy: string | null
  onStarted: (executionId: string) => void
  onRunNow: () => void
  onCreate: () => void
}) {
  const flow = info.flow
  const form = info.form
  if (flow.manual) {
    if (!form.exists) {
      if (!info.can_design) {
        return <Empty title="This flow is not set up yet"
          hint="Its input form has not been created. Ask the person who builds this application." />
      }
      return (
        <div style={{ ...cardStyle, padding: '18px 20px', maxWidth: 640 }}>
          <div style={{ fontSize: '0.9375rem', fontWeight: 600, marginBottom: 6 }}>Set up the input form</div>
          <div style={{ fontSize: '0.8825rem', color: C.ink2, lineHeight: 1.5, marginBottom: 12 }}>
            This flow takes {flow.inputs.length} input{flow.inputs.length === 1 ? '' : 's'}. Twasta
            makes them an entry screen — single values on the form, lists of rows as grids — that you
            can then design like any other screen: look-ups, item change, validations, defaults and layout.
            Nothing it collects is stored; Run sends it to the flow.
          </div>
          {flow.inputs.length > 0 && (
            <ul style={{ margin: '0 0 14px', paddingLeft: 18, fontSize: '0.8625rem', color: C.ink2 }}>
              {flow.inputs.slice(0, 12).map((f) => (
                <li key={f.name}>
                  <strong style={{ color: C.ink }}>{f.label}</strong>
                  {' — '}{KIND_LABEL[f.kind] || f.data_type}
                  {f.required ? ' · required' : ''}
                </li>
              ))}
              {flow.inputs.length > 12 && <li>…and {flow.inputs.length - 12} more</li>}
            </ul>
          )}
          <button type="button" className="btn btn-primary" onClick={onCreate} disabled={!!busy}
            data-tw-action="flow-create-form">
            <span className="material-icons" style={{ fontSize: 17 }}>dynamic_form</span>
            {busy === 'create' ? 'Creating…' : 'Create input form'}
          </button>
        </div>
      )
    }
    if (modelError) return <Empty title="The input form could not be loaded" hint={modelError} />
    if (!model) {
      return <div style={{ padding: '2rem', textAlign: 'center', color: C.ink2 }}>Loading the form…</div>
    }
    return (
      <div style={{ ...cardStyle, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column',
        overflow: 'hidden' }}>
        <FlowFormScreen key={formKey} project={project} objName={objName} model={model}
          title={flow.name} onStarted={onStarted} />
      </div>
    )
  }
  if (flow.scheduled) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 1, background: C.line, border: `1px solid ${C.line}`, borderRadius: 10, overflow: 'hidden' }}>
          <Band label="Next run" value={flow.next_run ? formatWhen(flow.next_run) : '—'}
            sub={flow.next_run ? formatRelative(flow.next_run) : ''} />
          <Band label="Cadence" value={flow.cadence_text || '—'}
            sub={flow.cadence?.cron_expression || ''} mono />
        </div>
        <DriftNotice drift={flow.timezone_drift} />
        {info.all_runs && (
          <div>
            <button type="button" className="btn btn-primary" disabled={!!busy} onClick={onRunNow}
              data-tw-action="flow-run-now">
              <span className="material-icons" style={{ fontSize: 17 }}>play_arrow</span>
              {busy === 'run' ? 'Starting…' : 'Run now'}
            </button>
          </div>
        )}
      </div>
    )
  }
  return <Empty title={`This flow starts on ${titleCase(flow.trigger_type)}`}
    hint="It runs on its own when that happens — nothing to fill in here. Its runs are listed under History." />
}

const KIND_LABEL: Record<string, string> = {
  scalar: 'a value', group: 'a group of values', table: 'rows (a grid)',
  list: 'a list', json: 'JSON', file: 'a file',
}

// ── Logs ─────────────────────────────────────────────────────────────────

function LogsTab({ run, following }: { run: FlowRun | null; following: boolean }) {
  if (!run) return <Empty title="No run yet" hint="Run the flow to watch it here, step by step." />
  const logs = run.logs || []
  return (
    <div style={{ ...cardStyle, overflow: 'auto' }}>
      <RunHeader run={run} following={following} />
      {logs.length === 0 ? (
        <div style={{ padding: '1.25rem', color: C.ink2, fontSize: '0.8825rem' }}>
          {following ? 'Waiting for IOFlow…' : 'IOFlow recorded no log lines for this run.'}
        </div>
      ) : logs.map((line, i) => (
        <div key={i} style={{ display: 'flex', gap: 10, padding: '6px 14px', fontSize: '0.8425rem',
          borderBottom: `1px solid ${C.line}` }}>
          <span style={{ fontFamily: 'var(--font-mono)', color: C.faint, flexShrink: 0 }}>
            {String(line.timestamp || '').slice(11, 19) || '—'}
          </span>
          <span style={{ flexShrink: 0, width: 54, fontWeight: 600, textTransform: 'uppercase',
            fontSize: '0.75rem', paddingTop: 2,
            color: line.level === 'error' ? C.err : line.level === 'warning' ? C.warn : C.faint }}>
            {line.level}
          </span>
          <span style={{ color: line.level === 'error' ? C.err : C.ink2, whiteSpace: 'pre-wrap',
            wordBreak: 'break-word' }}>{line.message}</span>
        </div>
      ))}
    </div>
  )
}

// ── Results ──────────────────────────────────────────────────────────────

function ResultsTab({ run, following }: { run: FlowRun | null; following: boolean }) {
  if (!run) return <Empty title="No result yet" hint="Run the flow to see its result here." />
  return (
    <div style={{ ...cardStyle, overflow: 'auto' }}>
      <RunHeader run={run} following={following} />
      <div style={{ padding: '14px 16px' }}>
        {!run.final ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: C.ink2, fontSize: '0.8825rem' }}>
            <div className="spinner" style={{ width: 16, height: 16 }} />
            Running{run.current_step ? ` — ${run.current_step}` : ''}…
          </div>
        ) : run.status === 'failed' ? (
          <div style={{ padding: '10px 12px', borderRadius: 8, background: C.errWeak,
            color: C.err, fontSize: '0.8825rem', whiteSpace: 'pre-wrap' }}>
            {run.error || 'The flow failed. See the Logs tab for where.'}
          </div>
        ) : run.result == null || run.result === '' ? (
          <div style={{ color: C.ink2, fontSize: '0.8825rem' }}>
            The flow finished but returned no data. Add a Response node to the flow in IOFlow to send a result back here.
          </div>
        ) : (
          <ResultView value={run.result} format={run.format || 'json'} />
        )}
        {run.inputs && Object.keys(run.inputs).length > 0 && (
          <details style={{ marginTop: 14 }}>
            <summary style={{ cursor: 'pointer', fontSize: '0.8425rem', color: C.ink2 }}>What was sent</summary>
            <pre style={preStyle}>{JSON.stringify(run.inputs, null, 2)}</pre>
          </details>
        )}
      </div>
    </div>
  )
}

const preStyle = {
  fontFamily: 'var(--font-mono)', fontSize: '0.8125rem', background: C.s2,
  border: `1px solid ${C.line}`, borderRadius: 6, padding: '10px 12px', color: C.ink,
  margin: '8px 0 0', overflow: 'auto', maxHeight: 460, whiteSpace: 'pre-wrap' as const,
  wordBreak: 'break-word' as const,
}

/** A run's result in the format the flow's response declared — IOFlow's own
 *  rendering. HTML goes through the shared rich-content allow-list first. */
function ResultView({ value, format }: { value: unknown; format: string }) {
  const text = typeof value === 'string' ? value : JSON.stringify(value, null, 2)
  const copy = () => {
    void navigator.clipboard?.writeText(text).then(
      () => toast.success('Copied'), () => toast.error('Could not copy'))
  }
  let body: ReactNode
  if (format === 'html') {
    body = <div className="flow-result-html" style={{ fontSize: '0.9125rem', lineHeight: 1.55 }}
      dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(text) }} />
  } else if (format === 'markdown') {
    body = <div style={{ fontSize: '0.9125rem', lineHeight: 1.55 }}>
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown></div>
  } else if (format === 'json' || typeof value !== 'string') {
    let pretty = text
    if (typeof value === 'string') {
      try { pretty = JSON.stringify(JSON.parse(value), null, 2) } catch { /* not JSON: as is */ }
    }
    body = <pre style={{ ...preStyle, margin: 0 }}>{pretty}</pre>
  } else {
    body = <div style={{ whiteSpace: 'pre-wrap', fontSize: '0.9125rem', lineHeight: 1.55 }}>{text}</div>
  }
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span style={labelStyle}>Result</span>
        <Chip tone="muted">{format}</Chip>
        <div style={{ flex: 1 }} />
        <button type="button" className="btn btn-ghost btn-sm" onClick={copy} data-tw-action="flow-copy-result">
          <span className="material-icons" style={{ fontSize: 15 }}>content_copy</span> Copy
        </button>
      </div>
      {body}
    </div>
  )
}

// ── History ──────────────────────────────────────────────────────────────

function HistoryTab({ runs, allRuns, selected, onOpen, onRefresh }: {
  runs: FlowRun[] | null
  allRuns: boolean
  selected: string | null
  onOpen: (r: FlowRun) => void
  onRefresh: () => void
}) {
  return (
    <div style={{ ...cardStyle, overflow: 'auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px',
        borderBottom: `1px solid ${C.line}` }}>
        <div style={{ fontSize: '0.875rem', fontWeight: 600, flex: 1 }}>
          {allRuns ? 'Every run of this flow' : 'Runs you started'}
        </div>
        <Chip tone="muted">from IOFlow</Chip>
        <button type="button" className="btn btn-ghost btn-sm" onClick={onRefresh} data-tw-action="refresh">
          <span className="material-icons" style={{ fontSize: 15 }}>refresh</span>
        </button>
      </div>
      {runs === null && (
        <div style={{ padding: '1.5rem', textAlign: 'center', color: C.ink2, fontSize: '0.8825rem' }}>Loading…</div>
      )}
      {runs?.length === 0 && (
        <Empty title="No runs yet" hint={allRuns
          ? 'IOFlow has not recorded a run of this flow.'
          : 'You have not run this flow yet.'} />
      )}
      {!!runs?.length && (
        <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.875rem' }}>
          <thead>
            <tr>
              {['Started', 'Trigger', 'Took', 'Status', 'Run'].map((h) => (
                <th key={h} style={{ textAlign: 'left', ...labelStyle, padding: '7px 14px',
                  borderBottom: `1px solid ${C.line}`, background: C.s2 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {runs.map((r) => (
              <tr key={r.execution_id} onClick={() => onOpen(r)} data-tw-row={r.execution_id}
                style={{ cursor: 'pointer',
                  background: selected === r.execution_id ? C.accentWeak : 'transparent' }}>
                <td style={cell(true)}>{formatWhen(r.started_at)}</td>
                <td style={cell()}>{titleCase(r.trigger_type)}</td>
                <td style={cell()}>{formatDuration(r.duration_seconds)}</td>
                <td style={cell()}><FlowStatus status={r.status} /></td>
                <td style={{ ...cell(), fontFamily: 'var(--font-mono)', fontSize: '0.8125rem' }}>
                  {String(r.execution_id || '').slice(0, 8)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div style={{ padding: '8px 14px', borderTop: `1px solid ${C.line}`, fontSize: '0.8125rem', color: C.faint }}>
        Runs and results are read live from IOFlow — Twasta keeps only who pressed Run.
      </div>
    </div>
  )
}

// ── bits ─────────────────────────────────────────────────────────────────

function RunHeader({ run, following }: { run: FlowRun; following: boolean }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
      borderBottom: `1px solid ${C.line}`, flexWrap: 'wrap' }}>
      <FlowStatus status={run.status} />
      <span style={{ fontSize: '0.8425rem', color: C.ink2 }}>
        {formatWhen(run.started_at)}
        {run.finished_at ? ` · took ${formatDuration(run.duration_seconds)}` : ''}
      </span>
      {following && <span style={{ fontSize: '0.8125rem', color: C.info }}>following…</span>}
      <div style={{ flex: 1 }} />
      <code style={{ fontSize: '0.8125rem', color: C.faint }}>{String(run.execution_id || '').slice(0, 8)}</code>
    </div>
  )
}

function Notice({ tone, icon, children }: { tone: 'warn' | 'info'; icon: string; children: ReactNode }) {
  const fg = tone === 'warn' ? C.warn : C.info
  const bg = tone === 'warn' ? C.warnWeak : C.infoWeak
  return (
    <div style={{ display: 'flex', gap: 9, alignItems: 'flex-start', padding: '10px 12px',
      borderRadius: 8, fontSize: '0.8425rem', background: bg, border: `1px solid ${fg}33`, color: fg }}>
      <span className="material-icons" style={{ fontSize: 17, flexShrink: 0 }}>{icon}</span>
      <div style={{ flex: 1 }}>{children}</div>
    </div>
  )
}

function TabButton({ active, onClick, children, hook }: {
  active: boolean; onClick: () => void; children: ReactNode; hook: string
}) {
  return (
    <button type="button" role="tab" aria-selected={active} onClick={onClick}
      data-tw-action={`flow-tab-${hook}`}
      style={{
        background: 'none', border: 'none', cursor: 'pointer', padding: '8px 14px',
        fontSize: '0.875rem', fontWeight: active ? 600 : 500,
        color: active ? C.accent : C.ink2,
        borderBottom: `2px solid ${active ? C.accent : 'transparent'}`, marginBottom: -1,
      }}>
      {children}
    </button>
  )
}

function cell(strong = false) {
  return {
    padding: '8px 14px', borderBottom: `1px solid ${C.line}`,
    color: strong ? C.ink : C.ink2, fontWeight: strong ? 500 : 400,
  }
}

function titleCase(s: string | null | undefined) {
  const t = String(s || '').replace(/_/g, ' ')
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : ''
}

function FlowStatus({ status }: { status: string }) {
  const s = String(status || '').toLowerCase()
  const tone = s === 'completed' ? 'ok'
    : s === 'failed' ? 'err'
    : s === 'running' ? 'info'
    : s === 'paused' ? 'warn'
    : 'muted'
  return <Chip tone={tone as 'ok' | 'err' | 'info' | 'warn' | 'muted'}>{titleCase(s) || '—'}</Chip>
}

function Band({ label, value, sub, mono }: {
  label: string; value: string; sub?: string; mono?: boolean
}) {
  return (
    <div style={{ background: C.panel, padding: '12px 14px' }}>
      <div style={labelStyle}>{label}</div>
      <div style={{ fontSize: '1.125rem', fontWeight: 600, lineHeight: 1.25, marginTop: 3 }}>{value}</div>
      {sub && <div style={{ fontSize: '0.8125rem', color: C.ink2, marginTop: 2,
        fontFamily: mono ? 'var(--font-mono)' : undefined }}>{sub}</div>}
    </div>
  )
}
